import { FC, useCallback } from 'react';
import { useMutation } from 'react-relay';
import { Form, Formik } from 'formik';
import graphql from 'babel-plugin-relay/macro';

import AddIcon from '@mui/icons-material/Add';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import TextField from 'components/TextField';
import SeoRouteTypeSelect from 'components/SeoRouteTypeSelect';

import { useDialog } from 'components/Dialog';
import { useErrorHandler } from 'lib';
import {
  SeoRoutesListAddMutation,
  SeoRouteType,
} from '__generated__/SeoRoutesListAddMutation.graphql';

type FormFields = {
  type: SeoRouteType;
  sourceUrl: string;
  targetUrl: string;
};

type Props = {
  connectionId: string;
};

const initial = {
  type: 'ALIAS' as SeoRouteType,
  sourceUrl: '',
  targetUrl: '',
};

const SeoRoutesListAdd: FC<Props> = ({ connectionId }) => {
  const { close } = useDialog();
  const errorHandler = useErrorHandler();

  const [add, loading] = useMutation<SeoRoutesListAddMutation>(graphql`
    mutation SeoRoutesListAddMutation(
      $input: CreateSeoRouteInput!
      $connections: [ID!]!
    ) {
      createSeoRoute(input: $input) {
        node
          @prependNode(
            connections: $connections
            edgeTypeName: "SeoRouteEdge"
          ) {
          ...SeoRoutesListRow_seoRoute
        }
      }
    }
  `);

  const handleSubmit = useCallback(
    (input: FormFields) => {
      add({
        variables: {
          input,
          connections: [connectionId],
        },

        ...errorHandler({
          onCompleted: () => {
            close();
          },
        }),
      });
    },
    [add, close, connectionId, errorHandler]
  );

  return (
    <Formik<FormFields> initialValues={initial} onSubmit={handleSubmit}>
      <Form>
        <DialogTitle>Naujas seo nuoroda</DialogTitle>

        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            Sukurkite naują seo nuorodą.
          </DialogContentText>

          <Stack spacing={4}>
            <SeoRouteTypeSelect name="type" />
            <TextField fullWidth name="sourceUrl" label="Ateinantis URL" />
            <TextField fullWidth name="targetUrl" label="Redirect URL" />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={close} variant="text">
            Atšaukti
          </Button>

          <LoadingButton
            loading={loading}
            type="submit"
            variant="contained"
            startIcon={<AddIcon />}>
            Pridėti
          </LoadingButton>
        </DialogActions>
      </Form>
    </Formik>
  );
};

export default SeoRoutesListAdd;
